import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { fetchTrips, deleteTrip, updateTrip, copyTrip as copyTripApi } from '../features/my-trips/api';
import { TripsListResponse, TripListItem } from '../features/my-trips/types';
import NameDialog from '../components/NameDialog';
import ScreenHeader from '../features/my-trips/components/ScreenHeader';
import InterestsWelcomeCard from '../features/my-trips/components/InterestsWelcomeCard';
import UnsortedJourneysCard from '../features/my-trips/components/UnsortedJourneysCard';
import TripCard from '../features/my-trips/components/TripCard';
import TripShareDialog from '../features/my-trips/components/TripShareDialog';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useDocumentTitle } from '../shared/useDocumentTitle';

// My Trips list page (design-handoff.md C.1). Phase 1: read-only list + interim
// navigation into the existing (filtered) journey list. The brand/avatar bar is
// the app's global Header.

const MyTripsPage: React.FC = () => {
  useDocumentTitle('My Trips');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [data, setData] = useState<TripsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<TripListItem | null>(null);
  // Kebab → Rename: a small dialog (shared with Create copy) rather than inline
  // editing, which is fiddly inside a whole-card-tappable trip card.
  const [renameTarget, setRenameTarget] = useState<TripListItem | null>(null);
  const [renaming, setRenaming] = useState(false);
  // Kebab → Share: same TripShareDialog as the trip/journey detail pages
  // (consistency call, Paulo 2026-09-12).
  const [shareTarget, setShareTarget] = useState<TripListItem | null>(null);
  // Kebab → Create copy: deep copy (trip + ALL alternatives) behind the
  // shared NameDialog, same as the trip detail page.
  const [copyTarget, setCopyTarget] = useState<TripListItem | null>(null);
  const [copying, setCopying] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const payload = await fetchTrips();
        if (active) setData(payload);
      } catch (e) {
        if (active) showToast({ title: 'Error', message: 'Failed to load your trips.', type: 'error' });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [showToast]);

  // Phase 2: TripCard now opens the real Trip detail page (replaces the Phase-1
  // interim hop into the filtered journey list).
  const openTrip = (trip: TripListItem) => navigate(`/trips/${encodeURIComponent(trip.id)}`);
  const editTrip = (trip: TripListItem) => navigate(`/trips/${encodeURIComponent(trip.id)}/edit`);
  // Deep copy: the trip AND every alternative, deterministic rows (Paulo
  // 2026-09-14 — replaces the old prefilled-form flow, which regenerated
  // via the LLM and never reproduced the original).
  const handleCopy = async (title: string) => {
    if (!copyTarget) return;
    setCopying(true);
    try {
      const created = await copyTripApi(copyTarget.id, title);
      setCopyTarget(null);
      showToast({ title: 'Copied', message: `"${title}" is ready, with all its routes.`, type: 'success', duration: 3000 });
      navigate(`/trips/${encodeURIComponent(created.tripId)}`);
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not copy the trip. Please try again.', type: 'error' });
    } finally {
      setCopying(false);
    }
  };

  const handleRename = async (title: string) => {
    if (!renameTarget) return;
    setRenaming(true);
    try {
      await updateTrip(renameTarget.id, { title });
      setData((prev) => (prev ? { ...prev, trips: prev.trips.map((t) => (t.id === renameTarget.id ? { ...t, title } : t)) } : prev));
      setRenameTarget(null);
      showToast({ title: 'Renamed', message: 'Trip name updated.', type: 'success', duration: 3000 });
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not rename the trip. Please try again.', type: 'error' });
    } finally {
      setRenaming(false);
    }
  };
  const openUnsorted = () => navigate('/trips/unsorted');

  // Delete a trip and its alternatives (disposition 'delete'), confirmed via the
  // shared ConfirmationDialog. On success, drop it from the list in place.
  const handleDeleteTrip = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteTrip(pendingDelete.id, 'delete');
      showToast({ title: 'Deleted', message: 'Trip deleted.', type: 'success' });
      setData((prev) => (prev ? { ...prev, trips: prev.trips.filter((t) => t.id !== pendingDelete.id) } : prev));
      setPendingDelete(null);
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || e?.message || 'Failed to delete trip', type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const trips = data?.trips ?? [];
  const unsortedCount = data?.unsorted.count ?? 0;
  const alternativesTotal = trips.reduce((sum, t) => sum + t.alternativesCount, 0);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '24px 16px 48px' }}>
        <ScreenHeader
          tripCount={trips.length}
          alternativesTotal={alternativesTotal}
          onNewTrip={() => navigate('/trips/new')}
        />

        {loading ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Loading your trips…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* One-time post-signup interests ask; self-hides once answered
                or dismissed (see the component's visibility rules). */}
            <InterestsWelcomeCard />

            <UnsortedJourneysCard count={unsortedCount} onOpen={openUnsorted} />

            {trips.length === 0 && unsortedCount === 0 ? (
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: 22,
                  padding: '32px 20px',
                  textAlign: 'center',
                  color: '#6c7872',
                }}
              >
                <div style={{ fontSize: 28, marginBottom: 8 }}>🧭</div>
                <div style={{ fontWeight: 600, color: '#16241d', marginBottom: 4 }}>No trips yet</div>
                <div style={{ fontSize: 14 }}>
                  Plan a journey, then file it under a trip to start organizing your travels.
                </div>
              </div>
            ) : (
              trips.map((trip) => (
                <TripCard key={trip.id} trip={trip} onOpen={openTrip} onShare={setShareTarget} onRename={setRenameTarget} onEdit={editTrip} onCopy={setCopyTarget} onDelete={setPendingDelete} />
              ))
            )}
          </div>
        )}
      </div>

      {renameTarget && (
        <NameDialog
          title="Rename trip"
          label="Trip name"
          initialValue={renameTarget.title}
          busy={renaming}
          onConfirm={handleRename}
          onCancel={() => setRenameTarget(null)}
        />
      )}

      {copyTarget && (
        <NameDialog
          title="Create a copy"
          label="Name for the trip copy"
          confirmText="Create copy"
          initialValue={`Copy of ${copyTarget.title}`}
          busy={copying}
          onConfirm={handleCopy}
          onCancel={() => { if (!copying) setCopyTarget(null); }}
        />
      )}

      {shareTarget && (
        <TripShareDialog
          tripId={shareTarget.id}
          tripTitle={shareTarget.title}
          source="my_trips"
          onClose={() => setShareTarget(null)}
        />
      )}

      {pendingDelete && (
        <ConfirmationDialog
          title="Delete this trip?"
          message={
            `"${pendingDelete.title}" and its ${pendingDelete.alternativesCount} ` +
            `route alternative${pendingDelete.alternativesCount === 1 ? '' : 's'} will be permanently deleted. This can't be undone.`
          }
          confirmText={deleting ? 'Deleting…' : 'Delete'}
          confirmButtonStyle="danger"
          onConfirm={handleDeleteTrip}
          onCancel={() => { if (!deleting) setPendingDelete(null); }}
        />
      )}
    </div>
  );
};

export default MyTripsPage;
